import { FeatureFlag } from '@ps/web-core-enums';
import type { CasinoMyRewards } from '@ps/web-martech-cms-types';
import { usePrevious } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useWidgetAnchor } from '@swf/casino';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import CloseButton, { Variant } from '@swf/components/StarsModal/components/CloseButton';
import { Header } from '@swf/core/enums';
import useChangeCurrency from '@swf/core/hooks/useChangeCurrency';
import useFeature from '@swf/core/hooks/useFeature';
import useUserState from '@swf/core/hooks/useUserState';
import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import { BonusStatus } from '@swf/core/types/CasinoBonus';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import type { InstantBonus } from '@swf/core/types/InstantBonus';
import type { RewardData, StarsCoinReward } from '@swf/core/types/StarsCoinReward';
import { Category } from '@swf/core/types/StarsCoinReward';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, hasAffordable, isInstantBonus } from '@swf/core/utils/casino';

import styles from './CasinoMyRewardsWidget.scss';
import CategorySelector from './components/CategorySelector/CategorySelector';
import FreeSpins from './components/FreeSpins/FreeSpins';
import InstantBonuses from './components/InstantBonuses/InstantBonuses';
import { createTimestamp } from './components/RefreshButton/RefreshButton';
import Rewards from './components/Rewards/Rewards';
import SpinOfTheDay from './components/SpinOfTheDay/SpinOfTheDay';
import type { SotDGameDesc } from './components/SpinOfTheDay/SpinOfTheDay';
import type WithClose from './components/WithClose';
import { casinoMyRewardsWidgetModel, useCasinoMyRewardsWidgetModel } from './model';

interface FreeSpinsData {
  games: CasinoGame[];
  subscriptions: { [path: string]: string };
}

interface Data {
  ib?: InstantBonus[];
  rewards?: RewardData;
  freeSpins?: FreeSpinsData;
  spinOfTheDay?: SotDGameDesc;
}

interface LocationState {
  reward?: StarsCoinReward;
  success?: boolean;
}

interface BodyProps extends WithClose {
  category: Category;
  data: Data;
  refreshTimestamp: number;
  spinOfDayEnabled: boolean;
  onReload(): void;
}

const Body: React.FC<BodyProps> = ({
  category,
  close,
  data,
  refreshTimestamp,
  spinOfDayEnabled,
  onReload
}) => {
  const { rewards, freeSpins: { games = [] } = {}, ib = [], spinOfTheDay } = data;

  switch (category) {
    case Category.FSB:
      return <FreeSpins games={games} close={close} />;
    case Category.Rewards:
      return <Rewards rewards={rewards?.rewards} close={close} />;
    case Category.IB:
      return (
        <InstantBonuses
          ib={ib}
          refreshTimestamp={refreshTimestamp}
          onReload={onReload}
          close={close}
        />
      );
    case Category.SpinOfDay:
      return spinOfDayEnabled ? <SpinOfTheDay close={close} data={spinOfTheDay} /> : null;
  }
};

export const MAX_SET_TIMEOUT = 0x7fffffff;

const isSotDAvailable = (spinOfTheDay: SotDGameDesc | undefined, widget: CasinoMyRewards) => {
  const sotdBonuses = spinOfTheDay ? Object.values(spinOfTheDay).map((value) => value) : [];

  return (
    widget?.spin_of_the_day?.config.enabled &&
    sotdBonuses.filter(
      (bonus) =>
        bonus.status === BonusStatus.Available ||
        (bonus.status === BonusStatus.Playing && bonus.numberOfSpins > 0)
    ).length > 0
  );
};

const CasinoMyRewardsWidget: WidgetFC = () => {
  const { widgetConfig, widgetDispatch, widgetSelect } = useCasinoMyRewardsWidgetModel();
  const balance = useSelector(select.account.getStarsCoinBalance);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const featureFSB = useFeature(FeatureFlag.CasinoFreeSpinsToken);
  const featureIB = useFeature(FeatureFlag.CasinoInstantBonusesToken);
  const featureRewards = useFeature(FeatureFlag.CasinoRewardsStoreToken);
  const featureSpinOfTheDay = useFeature(FeatureFlag.CasinoSpinOfDay) && !isPlayMoney;
  const isMobile = useSelector(select.device.isMobile);
  const currentModal = useSelector(select.modal.getCurrent);
  const oldBalance = usePrevious(balance);
  const widgetTargetId = useWidgetAnchor();
  const [refreshTimestamp, setRefreshTimestamp] = useUserState(createTimestamp());
  const [selected, setSelected] = useUserState<Category | undefined | null>(undefined);
  const data = useSelector(widgetSelect.get);

  const isLoading = useSelector((state: RootState) => widgetSelect.isLoading(state));

  const { rewards, freeSpins, ib, spinOfTheDay } = data;

  const performIBUpdate = useCallback(
    (timestamp = createTimestamp()) => {
      setRefreshTimestamp(timestamp);
      widgetDispatch.fetchIB();
    },
    [setRefreshTimestamp, widgetDispatch]
  );

  const available = Object.values(Category).filter((category) => {
    switch (category) {
      case Category.FSB:
        return freeSpins?.games?.length && featureFSB && widgetConfig.free_spins.config.enabled;
      case Category.Rewards:
        return (
          balance > 0 &&
          !!rewards?.rewards?.length &&
          hasAffordable({ rewards: rewards?.rewards, balance }) &&
          featureRewards &&
          widgetConfig.rewards.config.enabled
        );
      case Category.IB:
        return !!ib?.length && featureIB && widgetConfig.ib.config.enabled;
      case Category.SpinOfDay: {
        return featureSpinOfTheDay && isSotDAvailable(spinOfTheDay, widgetConfig);
      }
      default:
        return null;
    }
  });

  useEffect(() => {
    if (!isLoading) {
      if (selected === undefined && available.length) {
        if (available.findIndex((item) => item === selected) === -1 && selected !== null) {
          setSelected(available[0]);
        } else {
          setSelected(null);
        }
      } else if (!available.length && selected !== undefined) {
        setSelected(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available, isLoading]);

  useEffect(() => {
    if (currentModal) {
      const { path } = currentModal;
      const location = currentModal.locationState as LocationState;

      if (path === '/casino/buy-reward') {
        if (location.success && isInstantBonus(location.reward?.type)) {
          const now = createTimestamp();
          if (now - refreshTimestamp > 5) {
            performIBUpdate(now);
          }
        }
      }
    }
  }, [selected, currentModal, refreshTimestamp, performIBUpdate]);

  useEffect(() => {
    if (oldBalance !== balance) {
      widgetDispatch.fetchRewards();
      if (available.indexOf(Category.IB) !== -1) {
        widgetDispatch.fetchIB();
      }
    }
  }, [balance, available, oldBalance, widgetDispatch]);

  useChangeCurrency(() => widgetDispatch.fetchFreeSpins());

  const updateSelected = useCallback(
    (theCategory: Category) => {
      setSelected(theCategory);
      if (theCategory === Category.IB) {
        performIBUpdate();
      }
    },
    [setSelected, performIBUpdate]
  );

  if (selected != null && !available.includes(selected)) {
    setSelected(available.length > 0 ? available[0] : null);
  }

  return isAuthenticated && available.length ? (
    <section
      className={classNames(styles.container, isMobile && styles.mobile)}
      style={getStyle(widgetConfig.layout)}
      id={widgetTargetId}
    >
      <CacheControl>public, max-age=600</CacheControl>
      <Vary by={[Header.Currency]} />
      {available.length && (
        <CategorySelector onSelect={updateSelected} selected={selected} available={available} />
      )}
      {selected && (
        <Body
          category={selected}
          close={
            <CloseButton
              variant={Variant.Light}
              className={styles.closeButton}
              onClick={() => setSelected(null)}
            />
          }
          data={data}
          refreshTimestamp={refreshTimestamp}
          onReload={performIBUpdate}
          spinOfDayEnabled={featureSpinOfTheDay}
        />
      )}
    </section>
  ) : null;
};

CasinoMyRewardsWidget.model = casinoMyRewardsWidgetModel;

export default CasinoMyRewardsWidget;
